import { describeError, useApi } from '@classgo/app-kit';
import type { ReportCardListItem } from '@classgo/shared';
import { Badge, Button, Card } from '@classgo/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { refusalOf } from '../../attendance/api';
import { useT } from '../../i18n';
import { reportCardQueries, reportCardsApi } from '../../report-cards/api';
import { fetchBytes, saveFile } from '../../report-cards/download';

/**
 * The student's boletines (#116): one row per period of the year with how many subjects are
 * published and when the PDF was generated, and "Descargar PDF" for every period that has a
 * published grade (the API generates the file on the first download). Shown to whoever holds
 * `grades:read`, the key the API asks the staff for.
 */
export const ReportCardsCard = ({ studentId }: { studentId: string }) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const listQuery = reportCardQueries.ofStudent(api, studentId);
  const list = useQuery(listQuery);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const download = async (item: ReportCardListItem) => {
    setBusy(item.periodId);
    setErrors((current) => ({ ...current, [item.periodId]: '' }));
    try {
      const link = await reportCardsApi(api).download(studentId, item.periodId);
      saveFile(await fetchBytes(link.url), link.filename, 'application/pdf');
      if (!item.generatedAt) await queries.invalidateQueries({ queryKey: listQuery.queryKey });
    } catch (thrown) {
      const message =
        refusalOf(thrown) ??
        (thrown instanceof Error && thrown.message.startsWith('HTTP ')
          ? t('reportCards.download.failed')
          : describeError(t, thrown));
      setErrors((current) => ({ ...current, [item.periodId]: message }));
    } finally {
      setBusy(null);
    }
  };
  const items = list.data?.items ?? [];
  return (
    <Card title={t('student.reportCards')}>
      {list.isError ? (
        <p className="text-sm text-danger-700">{describeError(t, list.error)}</p>
      ) : !list.data ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">{t('student.reportCards.empty')}</p>
      ) : (
        <ul aria-label={t('student.reportCards')} className="divide-y divide-gray-100 text-sm">
          {items.map((item) => (
            <li
              key={item.periodId}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
            >
              <div>
                <p className="font-medium text-gray-900">
                  {item.periodName}
                  <Badge
                    tone={item.publishedSubjects > 0 ? 'success' : 'gray'}
                    className="ml-2 font-normal"
                  >
                    {item.publishedSubjects > 0
                      ? t('student.reportCards.published', { count: item.publishedSubjects })
                      : t('student.reportCards.none')}
                  </Badge>
                </p>
                <p className="text-xs text-gray-500">
                  {item.generatedAt
                    ? t('student.reportCards.generated', {
                        date: new Date(item.generatedAt).toLocaleString('es-CO'),
                      })
                    : t('student.reportCards.notGenerated')}
                </p>
                {errors[item.periodId] ? (
                  <p role="alert" className="text-danger-700">
                    {errors[item.periodId]}
                  </p>
                ) : null}
              </div>
              {item.publishedSubjects > 0 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  aria-label={t('student.reportCards.download.aria', { period: item.periodName })}
                  loading={busy === item.periodId}
                  disabled={busy !== null && busy !== item.periodId}
                  onClick={() => void download(item)}
                >
                  {t('student.reportCards.download')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};
